import React, {
  createContext,
  useState,
  useEffect,
  useContext,
  useRef,
} from "react";
import { auth, db } from "./firebase.config";
import { doc, getDoc } from "firebase/firestore";
import mixpanelService from "./MixpanelService";
import postHogService from "./PostHogService";
import PushService from "./PushService";
import RevenueCatService from "./RevenueCatService";

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [currentUserState, setCurrentUserState] = useState(null);
  const [loading, setLoading] = useState(true);
  const lastUserRef = useRef(null);

  // Function to set user properties in Mixpanel and PostHog
  const setMixpanelUserProperties = async (user) => {
    if (!user || user.isAnonymous) return;

    try {
      // Identify user in Mixpanel and PostHog
      mixpanelService.identify(user.uid);
      postHogService.identify(user.uid);

      // Set basic Firebase auth properties
      const userProperties = {
        user_id: user.uid,
        // Special Mixpanel properties ($ prefix)
        $phone: user.phoneNumber,
        // Custom properties
        phone: user.phoneNumber,
        signup_date: user.metadata.creationTime,
        last_login: user.metadata.lastSignInTime,
        is_anonymous: user.isAnonymous,
      };

      // Try to load additional profile data from Firestore
      try {
        const profileDocRef = doc(db, "public-profiles", user.uid);
        const profileDoc = await getDoc(profileDocRef);

        if (profileDoc.exists()) {
          const profileData = profileDoc.data();

          // Add profile data to user properties
          Object.assign(userProperties, {
            // Special Mixpanel properties ($ prefix)
            $name: profileData.displayName || profileData.username || "User",
            $username: profileData.username,

            // Custom properties
            username: profileData.username,
            display_name: profileData.displayName,
            bio: profileData.bio,
            has_photo: !!profileData.photoURL,
            friends_count: Array.isArray(profileData.friends)
              ? profileData.friends.length
              : 0,
            quizzes_shared: Array.isArray(profileData.shared)
              ? profileData.shared.length
              : 0,
            // Quiz completion status
            has_completed_first_date: !!profileData.firstDateQuizId,
            has_completed_political: !!profileData.politicalQuizId,
            has_completed_personality: !!profileData.myersBriggsQuizId,
            has_completed_sex_styles: !!profileData.sexStylesQuizId,
          });
        }
      } catch (profileError) {
        // Silently fail - profile loading for analytics is optional
      }

      // If we don't have profile data, at least set a basic name
      if (!userProperties.$name) {
        userProperties.$name = userProperties.phone || "User";
      }

      // Set user properties in Mixpanel and PostHog
      mixpanelService.setUserProperties(userProperties);
      postHogService.identify(user.uid, userProperties);

      // Track login event
      const loginEventProps = {
        user_id: user.uid,
        login_method: user.providerData[0]?.providerId || "unknown",
        is_new_session: true,
        timestamp: new Date().toISOString(),
      };
      mixpanelService.track("User Login", loginEventProps);
      postHogService.track("User Login", loginEventProps);
    } catch (error) {
      console.error("Error setting Mixpanel user properties:", error);
    }
  };

  useEffect(() => {
    console.log("AuthProvider: Setting up onAuthStateChanged...");

    const unsubscribe = auth.onAuthStateChanged(
      async (user) => {
        console.log("🔥 AuthProvider: onAuthStateChanged fired with user:", {
          user: user
            ? {
                uid: user.uid,
                isAnonymous: user.isAnonymous,
                phoneNumber: user.phoneNumber,
              }
            : null,
          timestamp: new Date().toISOString(),
        });

        try {
          if (user && !user.isAnonymous) {
            // User logged in - set Mixpanel properties
            await setMixpanelUserProperties(user);
            // Initialize push notifications silently if already permitted
            await PushService.initializeIfPermitted(user);
            lastUserRef.current = user;

            // Configure RevenueCat and identify user
            try {
              await RevenueCatService.configure(user.uid);
              await RevenueCatService.logIn(user.uid);
            } catch (e) {
              console.warn("RevenueCat identify failed", e);
            }
          } else if (!user) {
            // User logged out - reset Mixpanel and PostHog
            const logoutProps = {
              timestamp: new Date().toISOString(),
            };
            mixpanelService.track("User Logout", logoutProps);
            postHogService.track("User Logout", logoutProps);
            mixpanelService.reset();
            postHogService.reset();
            // Cleanup push token mapping (best-effort)
            try {
              await PushService.cleanupPushNotifications(lastUserRef.current);
            } catch (_) {}
            lastUserRef.current = null;
            // Log out from RevenueCat on sign-out
            try {
              await RevenueCatService.logOut();
            } catch (_) {}
          }

          setCurrentUserState(user || null);
        } catch (error) {
          console.error("Error setting user state:", error);
          setCurrentUserState(null);
        } finally {
          setLoading(false);
        }
      },
      (error) => {
        console.error("Auth state change error:", error);
        setCurrentUserState(null);
        setLoading(false);
      }
    );

    return unsubscribe; // Clean up the listener
  }, []);

  return (
    <AuthContext.Provider value={{ currentUserState, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);

// Utility function to refresh user properties in Mixpanel and PostHog (call this when profile is updated)
export const refreshMixpanelUserProperties = async (userId) => {
  if (!userId) return;

  try {
    const profileDocRef = doc(db, "public-profiles", userId);
    const profileDoc = await getDoc(profileDocRef);

    if (profileDoc.exists()) {
      const profileData = profileDoc.data();

      const updatedProperties = {
        // Special Mixpanel properties ($ prefix)
        $name: profileData.displayName || profileData.username || "User",
        $username: profileData.username,

        // Custom properties
        username: profileData.username,
        display_name: profileData.displayName,
        bio: profileData.bio,
        has_photo: !!profileData.photoURL,
        friends_count: Array.isArray(profileData.friends)
          ? profileData.friends.length
          : 0,
        quizzes_shared: Array.isArray(profileData.shared)
          ? profileData.shared.length
          : 0,
        // Quiz completion status
        has_completed_first_date: !!profileData.firstDateQuizId,
        has_completed_political: !!profileData.politicalQuizId,
        has_completed_personality: !!profileData.myersBriggsQuizId,
        has_completed_sex_styles: !!profileData.sexStylesQuizId,
        last_updated: new Date().toISOString(),
      };

      mixpanelService.setUserProperties(updatedProperties);
      postHogService.identify(userId, updatedProperties);
      console.log("✅ Mixpanel and PostHog user properties refreshed");
    }
  } catch (error) {
    console.error("Error refreshing Mixpanel user properties:", error);
  }
};
